import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { auth } from "@/api/client";

import i18n, { followServerLanguage, uiLanguage } from "@/i18n";
import { setDateTimeLocale } from "@/i18n/datetime";

import { useBranding } from "./branding";

export interface CurrentUser {
    id: number;
    name: string;
    login: string;
    partner_id?: number;
    partner_write_date?: string | false;
    is_admin: boolean;
    lang: string;
    ui_lang: string | null;
    direction: "ltr" | "rtl";
    date_format?: string;
    time_format?: string;
    company: { id: number; name: string } | null;
    companies: { id: number; name: string }[];
}

interface AuthState {
    user: CurrentUser | null;
    loading: boolean;
    /** where the identity provider sends someone with no session */
    loginUrl: string | null;
    signOut(): Promise<void>;
    chooseCompany(companyId: number): Promise<void>;
    chooseLanguage(code: string): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

function followFormats(user: { lang?: string; date_format?: string; time_format?: string }) {
    if (user.lang) {
        setDateTimeLocale(user.lang, user.date_format, user.time_format);
    }
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const { reload: reloadBranding } = useBranding();
    const [user, setUser] = useState<CurrentUser | null>(null);
    const [loginUrl, setLoginUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const controller = new AbortController();
        auth.me(controller.signal)
            .then((payload) => {
                followFormats(payload.data);
                setUser(payload.data as CurrentUser);
                return followServerLanguage(payload.data.lang);
            })
            .catch((error: Error) => {
                if (error.name === "AbortError") {
                    return;
                }
                setUser(null);
                setLoginUrl((error as { loginUrl?: string }).loginUrl ?? null);
            })
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, []);

    const value = useMemo<AuthState>(() => ({
        user,
        loading,
        loginUrl,
        async chooseCompany(companyId: number) {
            await auth.chooseCompany(companyId);
            const payload = await auth.me();
            followFormats(payload.data);
            setUser(payload.data as CurrentUser);
            await reloadBranding();
        },
        async chooseLanguage(code: string) {
            const payload = await auth.chooseLanguage(code);
            followFormats(payload.data);
            setUser((current) => (current
                ? { ...current, lang: payload.data.lang, ui_lang: payload.data.ui_lang,
                    direction: payload.data.direction, date_format: payload.data.date_format,
                    time_format: payload.data.time_format }
                : current));
            await i18n.changeLanguage(uiLanguage(payload.data.lang) ?? code);
        },
        async signOut() {
            const where = auth.logoutUrl();
            window.location.assign(where);
        },
    }), [user, loading, loginUrl, reloadBranding]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
    const value = useContext(AuthContext);
    if (!value) {
        throw new Error("useAuth must be used inside AuthProvider");
    }
    return value;
}
